import axios from 'axios';
import { VerbaScholaApi } from '../api/VerbaScholaApi';
import type { IVerbaScholaAiReviewEdit } from '../api/VerbaScholaLessonAiApi';
import { ACTIVITY_EDIT_KINDS, ReviewEdit, ReviewEditKind } from './proposalReviewModel';

type Update = (id: number, data: Record<string, unknown>) => Promise<unknown>;

const updates: Record<ReviewEditKind, Update> = {
    lesson: (id, data) => VerbaScholaApi.updateLesson(id, data),
    plan_section: (id, data) => VerbaScholaApi.updateLessonPlanSection(id, data),
    generated_material: (id, data) => VerbaScholaApi.updateLessonGeneratedMaterial(id, data),
    vocabulary_target: (id, data) => VerbaScholaApi.updateLessonVocabularyTarget(id, data),
    grammar_target: (id, data) => VerbaScholaApi.updateLessonGrammarTarget(id, data),
    exercise: (id, data) => VerbaScholaApi.updateLessonExercise(id, data),
    exercise_step: (id, data) => VerbaScholaApi.updateLessonExerciseStep(id, data),
    exercise_step_choice: (id, data) => VerbaScholaApi.updateLessonChoice(id, data as Parameters<typeof VerbaScholaApi.updateLessonChoice>[1]),
    exercise_step_sentence: (id, data) => VerbaScholaApi.updateLessonSentenceLink(id, data as Parameters<typeof VerbaScholaApi.updateLessonSentenceLink>[1]),
    exercise_step_conversation: (id, data) => VerbaScholaApi.updateLessonConversationName(id, data as Parameters<typeof VerbaScholaApi.updateLessonConversationName>[1]),
};

/** The lesson's current rows of one kind, to check edited fields before writing them. */
async function currentRows(lessonId: number, kind: Exclude<ReviewEditKind, 'lesson'>): Promise<{ id: number }[]> {
    switch (kind) {
    case 'plan_section': return (await VerbaScholaApi.getLessonPlanSectionList(lessonId)).data;
    case 'generated_material': return (await VerbaScholaApi.getLessonGeneratedMaterialList(lessonId)).data;
    case 'vocabulary_target': return (await VerbaScholaApi.getLessonVocabularyTargetList(lessonId)).data;
    case 'grammar_target': return (await VerbaScholaApi.getLessonGrammarTargetList(lessonId)).data;
    case 'exercise': return (await VerbaScholaApi.getLessonExerciseList(lessonId)).data;
    case 'exercise_step': return (await VerbaScholaApi.getLessonExerciseContent(lessonId)).data.steps;
    case 'exercise_step_choice': return (await VerbaScholaApi.getLessonExerciseContent(lessonId)).data.choices;
    // A line's or speaking target's sentence, as its edit names it.
    case 'exercise_step_sentence': return ((await VerbaScholaApi.getLessonExerciseContent(lessonId)).data.sentences ?? [])
        .map(row => ({ ...row, sentence_text: row.text, sentence_pinyin: row.pinyin }));
    case 'exercise_step_conversation': return (await VerbaScholaApi.getLessonExerciseContent(lessonId)).data.conversations ?? [];
    }
}

// Fields the lesson form and the exercise fields keep as typed; the saved rows trim them, as the pages' own saves do.
const typedText: Partial<Record<ReviewEditKind, string[]>> = {
    lesson: ['brief', 'description', 'ai_context_prompt_text'],
    exercise: ['subtitle', 'purpose', 'teacher_instructions', 'student_instructions'],
};

function payload(kind: ReviewEditKind, fields: Record<string, unknown>): Record<string, unknown> {
    const text = (value: unknown) => typeof value === 'string' ? (value.trim() || null) : value;
    // An activity's texts are trimmed as its editor's own save does; a dialogue without a name has none.
    if (ACTIVITY_EDIT_KINDS.has(kind)) return Object.fromEntries(Object.entries(fields).map(([field, value]) =>
        [field, kind === 'exercise_step_conversation' && field === 'name' ? text(value) : typeof value === 'string' ? value.trim() : value]));
    return Object.fromEntries(Object.entries(fields).map(([field, value]) =>
        [field, field === 'title' && typeof value === 'string' && typedText[kind] ? value.trim() : typedText[kind]?.includes(field) ? text(value) : value]));
}

// An empty value reads the same whether the server stores null, an empty string or an empty list.
const blank = (value: unknown) => value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
/** The saved-result answers that mean the proposal created no rows. */
const noSavedResult = new Set(['proposal_preview_unavailable', 'generation_not_found']);
const same = (a: unknown, b: unknown) => (blank(a) && blank(b)) || JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// An activity's blanks and line translations, keyed by line id inside its ``speaking_content_json``.
const LINE_MAPS = ['blank_text_by_line_id', 'line_english_by_id'];
const isMap = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
/**
 * Whether a field's value as Your version showed it cannot be checked on the lesson: a new activity's line maps are keyed
 * by ids only the save that creates its lines gives (the review showed the projection's temporary ones).
 */
const unchecked = (edit: ReviewEdit, field: string) => Boolean(edit.clientKey) && edit.kind === 'exercise_step' && field === 'speaking_content_json';
/** What an edit sends as ``expected``: none of its line maps for a row the same save creates. */
function sentExpected(edit: PendingReviewEdit): Record<string, unknown> | undefined {
    if (!edit.expected) return undefined;
    const expected = Object.fromEntries(Object.entries(edit.expected).filter(([field]) => !unchecked(edit, field)));
    return Object.keys(expected).length ? expected : undefined;
}
/** The draft keys of the lines a step edit's line maps name: lines the same save was to create (saved lines go by id). */
function draftLineKeys(edit: ReviewEdit): string[] {
    const options = edit.kind === 'exercise_step' ? edit.fields.speaking_content_json : undefined;
    return isMap(options) ? LINE_MAPS.flatMap(map => { const byId = options[map]; return isMap(byId) ? Object.keys(byId).filter(key => !/^\d+$/.test(key)) : []; }) : [];
}
/** A step edit's fields with each line named by its draft key under the id the save that created it gave it. */
function withSavedLineIds(fields: Record<string, unknown>, created: Record<string, number>): Record<string, unknown> {
    const options = fields.speaking_content_json;
    if (!isMap(options)) return fields;
    const speaking = { ...options };
    for (const map of LINE_MAPS) {
        const byId = speaking[map];
        if (isMap(byId)) speaking[map] = Object.fromEntries(Object.entries(byId).map(([key, value]) => [key in created ? String(created[key]) : key, value]));
    }
    return { ...fields, speaking_content_json: speaking };
}

/** The edits as the review save takes them (written with the AI changes in one transaction): trimmed as the pages' saves do. */
export function serverEdits(edits: PendingReviewEdit[]): IVerbaScholaAiReviewEdit[] {
    const ref = (row: { id?: number; clientKey?: string }) => row.clientKey ? { client_key: row.clientKey } : { id: row.id! };
    return edits.map(edit => {
        if (edit.action === 'create') return { action: 'create', kind: edit.kind, client_key: edit.clientKey, parent: { kind: edit.parent!.kind, ...ref(edit.parent!) },
            fields: payload(edit.kind, edit.fields) };
        if (edit.action === 'delete') return { action: 'delete', kind: edit.kind, ...ref(edit), ...(edit.expected && !edit.clientKey ? { expected: edit.expected } : {}) };
        if (edit.action === 'order') return { action: 'order', kind: edit.kind, parent: { kind: edit.parent!.kind, ...ref(edit.parent!) }, order: (edit.order ?? []).map(ref),
            ...(edit.expected && !edit.parent?.clientKey ? { expected: edit.expected } : {}) };
        const expected = sentExpected(edit);
        return { kind: edit.kind, ...(edit.kind === 'lesson' ? {} : ref(edit)), fields: payload(edit.kind, edit.fields), ...(expected ? { expected } : {}) };
    });
}

/**
 * What each edited field must hold for an edit to be written on its own: its ``expected`` value, or, where that is not
 * known (an activity's content a chosen AI change rewrites, which only the server's projection shows), the value the
 * review showed as saved. Another save's content there is listed, never overwritten.
 */
function recoveryExpected(edit: PendingReviewEdit, createdLines: Record<string, number>): Record<string, unknown> | null {
    const shown = Object.fromEntries(Object.entries(edit.before ?? {}).filter(([field]) => field in edit.fields));
    const expected = { ...shown, ...(sentExpected(edit) ?? {}) };
    if (!unchecked(edit, 'speaking_content_json') || !('speaking_content_json' in expected)) return expected;
    // A new activity's settings are checked like any others, its lines under the ids the save that created them gave
    // them. A line no save created, or one shown under the projection's temporary id, cannot be checked: null, and the
    // edit is listed for the teacher to redo instead of written over what is there.
    const options = expected.speaking_content_json;
    const keys = isMap(options) ? LINE_MAPS.flatMap(map => isMap(options[map]) ? Object.keys(options[map] as Record<string, unknown>) : []) : [];
    if (keys.some(key => /^\d+$/.test(key) || !(key in createdLines))) return null;
    return withSavedLineIds(expected, createdLines);
}

/** An edit waiting to be written, with the values its fields must still hold on the lesson for it to be written. */
export interface PendingReviewEdit extends ReviewEdit {
    /** Saved or AI-written values the lesson must hold in the edited fields (for a row the save creates, what the AI proposed, or empty). */
    expected?: Record<string, unknown>;
}

export interface SavedReviewEdits {
    /** Edits not written because of an error (and every one after it), with its message. */
    unsaved: PendingReviewEdit[];
    error: string | null;
    /** Edits not written because the lesson changed those fields since the review showed them. */
    conflicts: PendingReviewEdit[];
}

/**
 * Writes a review's manual edits, one row at a time, after its AI changes were saved (or with none kept). A proposed
 * new row is found by its draft key in the saved result's ids.
 *
 * Each write reads the lesson's revision first, then the row, and sends that same revision: a change made after the
 * read fails the write instead of being overwritten, and a change made before it shows in the row. An edit whose
 * fields no longer hold their ``expected`` values is skipped as a conflict. An edit already on the lesson (a save
 * interrupted by a reload) counts as written. ``onWritten`` reports each edit written.
 */
export async function saveReviewEdits(lessonId: number, generationId: number | null, edits: PendingReviewEdit[],
    describeError: (caught: unknown) => string, onWritten: (edit: PendingReviewEdit) => void = () => undefined): Promise<SavedReviewEdits> {
    // Rows the AI save created are found by their draft keys in its saved result. Only the server's own answer that the
    // proposal has none (it was not saved: dismissed, or saved from another choice; or it is gone) means no rows were
    // created, so edits to them are listed. Any other failure (an expired login, a server error, no answer) keeps those
    // edits to try again. Edits to saved rows are written either way.
    let savedIds: Record<string, Record<string, number>> | null = {};
    let lookupError: string | null = null;
    if (generationId !== null && edits.some(edit => edit.clientKey || draftLineKeys(edit).length)) {
        try {
            savedIds = (await VerbaScholaApi.getLessonAiSavedResult(lessonId, generationId)).data.saved_target_ids ?? {};
        } catch (caught) {
            const code = axios.isAxiosError(caught) ? caught.response?.data?.error_code : undefined;
            if (!noSavedResult.has(String(code))) { savedIds = null; lookupError = describeError(caught); }
        }
    }
    const conflicts: PendingReviewEdit[] = [];
    const waiting: PendingReviewEdit[] = [];
    for (let index = 0; index < edits.length; index++) {
        const edit = edits[index];
        // An added, removed or reordered answer or line was part of Your version's own save, which was not made: it is
        // listed to redo on the page, never counted as written.
        if (edit.action) { conflicts.push(edit); continue; }
        const lines = draftLineKeys(edit);
        if ((edit.clientKey || lines.length) && savedIds === null) { waiting.push(edit); continue; }
        const id = edit.kind === 'lesson' ? lessonId : edit.clientKey ? savedIds?.[edit.kind]?.[edit.clientKey] : edit.id;
        // A new row the AI save did not create (another choice of changes was saved) cannot take the edit: it is listed.
        if (id === undefined) { conflicts.push(edit); continue; }
        // So is a step edit naming a line no save created (one the teacher added here, or a new line of another choice).
        const createdLines = savedIds?.exercise_step_sentence ?? {};
        if (lines.some(key => !(key in createdLines))) { conflicts.push(edit); continue; }
        try {
            const lesson = (await VerbaScholaApi.getLesson(lessonId)).data;
            const revision = lesson.authoring_revision;
            const row = (edit.kind === 'lesson' ? lesson : (await currentRows(lessonId, edit.kind)).find(item => item.id === id)) as Record<string, unknown> | undefined;
            // A line the AI save created goes by the id it was given.
            const fields = lines.length ? withSavedLineIds(payload(edit.kind, edit.fields), createdLines) : payload(edit.kind, edit.fields);
            if (!row) { conflicts.push(edit); continue; }
            if (Object.entries(fields).every(([field, value]) => same(row[field], value))) { onWritten(edit); continue; }
            const check = recoveryExpected(edit, createdLines);
            if (check === null || Object.entries(check).some(([field, value]) => !same(row[field], value))) { conflicts.push(edit); continue; }
            await updates[edit.kind](id, { ...fields, expected_authoring_revision: revision });
            onWritten(edit);
        } catch (caught) {
            return { unsaved: [...waiting, ...edits.slice(index)], error: describeError(caught), conflicts };
        }
    }
    return { unsaved: waiting, error: waiting.length ? lookupError : null, conflicts };
}
